/**
 * CompositorPrint.js
 * Print-ready canvas generation for Compositor
 */

import { ContentRenderer } from "./ContentRenderer.js";
import { CYODesign } from "./CYODesign.js";
import {
    getOpaqueBounds,
    loadWalletGripMask,
    createInvertedMask,
    createScaledMask
} from "./CompositorMasks.js";

/**
 * Creates a print-ready canvas containing only content (background + user image + stickers)
 * without product image, mask, or overlay visible in the final result
 *
 * This method is specifically designed for generating print-ready images that will be
 * sent to the printer. It uses the product mask for proper content masking but excludes
 * the product image, mask, and overlay from the final output.
 *
 * The resulting canvas contains:
 * - Background image (if any)
 * - User uploaded image (if any)
 * - Sticker set (if any)
 * All properly masked to the device shape but without the product visuals.
 *
 * @param {Object} device - The device to render
 * @param {Object} cyoDesign - Gallery options containing render settings
 * @param {Object} layerTransformData - Transform data for layers (optional)
 * @param {string} productImageID - The product image ID to use for masking
 * @param {number} randomSeed - Random seed for deterministic rendering (optional)
 * @param {Object} altDevice - Alternative device for grip surface in independent both mode (optional)
 * @param {Object} altLayerTransformData - Transform data for alt device layers (optional)
 * @param {Function} loadDeviceImages - Function to load device images (Compositor.loadDeviceImages)
 * @returns {HTMLCanvasElement} A print-ready canvas with masked content only
 */
export async function createPrintReadyCanvas(device, cyoDesign, layerTransformData = null, productImageID = null, randomSeed = null, altDevice = null, altLayerTransformData = null, loadDeviceImages) {
    // Use device.imageID as fallback if no specific productImageID provided
    const finalProductImageID = productImageID || device.imageID;

    // Check if this is independent both mode (each surface renders its own treatment)
    const isBothModeIndependent = altDevice && cyoDesign.requiresIndependentSurfaceRendering?.(device);

    // Load device images for masking (we need product and mask images for proper masking)
    // Use high resolution for print: 10000px width, 100% quality
    const { productImage, maskImage } = await loadDeviceImages(device, finalProductImageID, null, 10000, 100);

    // Create ContentRenderer in print mode with deterministic randomness
    const contentRenderer = new ContentRenderer(device, cyoDesign, true, randomSeed); // true = print mode
    contentRenderer.stickerImages = cyoDesign.stickerImages || [];
    contentRenderer.backgroundImage = cyoDesign.backgroundImage || null;
    contentRenderer.transparentImage = cyoDesign.transparentImage || null;
    contentRenderer.userImage = cyoDesign.userImage || null;

    // In split mode, wallet surface stickers should not reserve space for user image
    // Only apply to wallet device, not to grip device (altCompositor)
    if (cyoDesign.walletPrintSurface === CYODesign.WalletPrintSurface.split && device.productCategory === "wallet") {
        contentRenderer.ignorePhotoPlacement = true;
    }

    // In split mode, grip surface should not reserve space for stickers
    // Only apply to grip device (altCompositor), not wallet device
    if (cyoDesign.walletPrintSurface === CYODesign.WalletPrintSurface.split && device.productCategory === "grip") {
        contentRenderer.ignoreStickerPlacement = true;
    }

    // Pass loaded device images to ContentRenderer for masking
    contentRenderer.clientProductImage = productImage;
    contentRenderer.clientProductMaskImage = maskImage;

    // Generate the layer tree (print mode will exclude product/overlay layers)
    const layerTree = await contentRenderer.draw();

    // Extract dimensions from the mask layer (which is always available)
    const maskLayer = layerTree.mask;
    if (!maskLayer) {
        throw new Error("Mask layer not available for print generation");
    }
    const width = maskLayer.width;
    const height = maskLayer.height;

    // Calculate scale factor for transforms from preview canvas to print canvas
    // Transforms are extracted in preview canvas pixel space and need to be scaled
    let transformScaleX = 1;
    let transformScaleY = 1;
    if (layerTransformData && layerTransformData.sourceWidth && layerTransformData.sourceHeight) {
        transformScaleX = width / layerTransformData.sourceWidth;
        transformScaleY = height / layerTransformData.sourceHeight;
    }
    // Calculate final print canvas dimensions based on bleed insets
    let finalWidth = width;
    let finalHeight = height;
    let cropX = 0;
    let cropY = 0;
    let cropWidth = width;
    let cropHeight = height;

    if (device.bleedInsets && Array.isArray(device.bleedInsets) && device.bleedInsets.length === 4) {
        const [topInset, rightInset, bottomInset, leftInset] = device.bleedInsets;

        // Calculate crop rectangle from bleed insets (ratios to pixels)
        cropX = leftInset * width;
        cropY = topInset * height;
        cropWidth = width - (leftInset + rightInset) * width;
        cropHeight = height - (topInset + bottomInset) * height;

        // Final canvas should be sized to the cropped area to avoid aspect ratio distortion
        finalWidth = cropWidth;
        finalHeight = cropHeight;
    }

    // Create final canvas for print output
    const printCanvas = document.createElement("canvas");
    printCanvas.width = finalWidth;
    printCanvas.height = finalHeight;
    const printCtx = printCanvas.getContext("2d", { alpha: true });

    // Create a temp canvas for content that will be masked
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempCtx = tempCanvas.getContext("2d", { alpha: true });

    // In split mode for grip surface, skip background image (it goes on wallet only)
    const skipBackgroundImage = cyoDesign.walletPrintSurface === CYODesign.WalletPrintSurface.split &&
                                device.productCategory === "grip";

    // Draw background image first (if available)
    if (layerTree.bgImage && !skipBackgroundImage) {
        tempCtx.drawImage(layerTree.bgImage, 0, 0);
    }

    // Helper function to draw user image with transforms
    const drawUserImageWithTransforms = (canvas) => {
        if (!canvas) return;

        // Check if this is an expanded canvas that needs special positioning
        let baseOffsetX = 0;
        let baseOffsetY = 0;

        if (canvas._isExpandedCanvas) {
            // Apply the viewport offset to center the expanded canvas content
            baseOffsetX = -(canvas._viewportOffsetX || 0);
            baseOffsetY = -(canvas._viewportOffsetY || 0);
        }

        // Check if we have user image transforms to apply
        if (layerTransformData && layerTransformData.userImage) {
            const transform = layerTransformData.userImage;

            // Only apply transforms if they're not default values
            if (transform.scale !== 1 || transform.rotate !== 0 || transform.translateX !== 0 || transform.translateY !== 0) {
                tempCtx.save();

                // Calculate transform origin (pivot point for rotation/scale)
                // For expanded canvases: use stored rotation pivot (center of visible image)
                // For non-expanded: use visualCenter from preview (scaled) if available, else canvas center
                let centerX, centerY;
                if (canvas._isExpandedCanvas) {
                    // Use stored rotation pivot which is the center of visible image portion
                    centerX = canvas._rotationPivotX ?? canvas._originalCanvasWidth / 2;
                    centerY = canvas._rotationPivotY ?? canvas._originalCanvasHeight / 2;
                } else if (transform.visualCenterX !== undefined && transform.visualCenterY !== undefined) {
                    // Use the visualCenter from preview, scaled to print dimensions
                    centerX = transform.visualCenterX * transformScaleX;
                    centerY = transform.visualCenterY * transformScaleY;
                } else {
                    centerX = width / 2;
                    centerY = height / 2;
                }

                // Scale translations from preview canvas space to print canvas space
                const scaledTranslateX = transform.translateX * transformScaleX;
                const scaledTranslateY = transform.translateY * transformScaleY;

                // Apply transforms in correct order: translate to center + user translation, rotate, scale, translate back
                tempCtx.translate(centerX + scaledTranslateX, centerY + scaledTranslateY);
                tempCtx.rotate(transform.rotate * Math.PI / 180);
                tempCtx.scale(transform.scale, transform.scale);
                tempCtx.translate(-centerX, -centerY);

                tempCtx.drawImage(canvas, baseOffsetX, baseOffsetY);
                tempCtx.restore();
            } else {
                // No significant transforms, but still apply viewport offset for expanded canvases
                tempCtx.drawImage(canvas, baseOffsetX, baseOffsetY);
            }
        } else {
            // No transform data, but still apply viewport offset for expanded canvases
            tempCtx.drawImage(canvas, baseOffsetX, baseOffsetY);
        }
    };

    // Helper function to draw stickers with transforms
    const drawStickersWithTransforms = (canvas) => {
        if (!canvas) return;

        // Check if we have sticker transforms to apply
        if (layerTransformData && layerTransformData.stickers) {
            const transform = layerTransformData.stickers;

            // Only apply transforms if they're not default values
            if (transform.scale !== 1 || transform.rotate !== 0 || transform.translateX !== 0 || transform.translateY !== 0) {
                tempCtx.save();

                // Calculate transform origin (center of canvas)
                const centerX = width / 2;
                const centerY = height / 2;

                // Scale translations from preview canvas space to print canvas space
                const scaledTranslateX = transform.translateX * transformScaleX;
                const scaledTranslateY = transform.translateY * transformScaleY;

                // Apply transforms in correct order: translate to center + user translation, rotate, scale, translate back
                tempCtx.translate(centerX + scaledTranslateX, centerY + scaledTranslateY);
                tempCtx.rotate(transform.rotate * Math.PI / 180);
                tempCtx.scale(transform.scale, transform.scale);
                tempCtx.translate(-centerX, -centerY);

                tempCtx.drawImage(canvas, 0, 0);
                tempCtx.restore();
            } else {
                // No significant transforms, draw normally
                tempCtx.drawImage(canvas, 0, 0);
            }
        } else {
            // No transform data, draw normally
            tempCtx.drawImage(canvas, 0, 0);
        }
    };

    // In split mode for wallet surface, skip user image (it goes on grip only)
    const skipUserImage = cyoDesign.walletPrintSurface === CYODesign.WalletPrintSurface.split &&
                          device.productCategory === "wallet";
    // In split mode for grip surface, skip stickers (they go on wallet only)
    const skipStickers = cyoDesign.walletPrintSurface === CYODesign.WalletPrintSurface.split &&
                         device.productCategory === "grip";

    // Determine layer order based on design options
    if (cyoDesign && layerTree.userImage && layerTree.stickers) {
        const userImgVariant = cyoDesign.userImage;
        const stickerImages = cyoDesign.stickerImages || [];

        // Check what should draw on top using the same logic as Compositor
        let whatOnTop = cyoDesign.whatToDrawOnTop(userImgVariant, stickerImages);

        // For wallet products with removeBackground + center/bottom, user image goes on top
        const isWalletProduct = device?.productCategory === "wallet";
        const isRemoveBgCenterOrBottom = cyoDesign.subjectStyle === CYODesign.Style.removeBackground &&
            (cyoDesign.subjectPlacement === CYODesign.Placement.center || cyoDesign.subjectPlacement === CYODesign.Placement.bottom);
        if (isWalletProduct && isRemoveBgCenterOrBottom) {
            whatOnTop = CYODesign.StickerOrPhoto.photo;
        }

        if (whatOnTop === CYODesign.StickerOrPhoto.sticker) {
            // Stickers on top - draw user image first, then stickers
            if (!skipUserImage) {
                drawUserImageWithTransforms(layerTree.userImage);
                if (layerTree.photoStickers) {
                    tempCtx.drawImage(layerTree.photoStickers, 0, 0);
                }
            }
            if (!skipStickers) {
                drawStickersWithTransforms(layerTree.stickers);
            }
        } else {
            // User image on top - draw stickers first, then user image
            if (!skipStickers) {
                drawStickersWithTransforms(layerTree.stickers);
            }
            if (!skipUserImage) {
                if (layerTree.photoStickers) {
                    tempCtx.drawImage(layerTree.photoStickers, 0, 0);
                }
                drawUserImageWithTransforms(layerTree.userImage);
            }
        }
    } else {
        // Fallback to original order if we don't have the necessary data
        if (!skipStickers) {
            drawStickersWithTransforms(layerTree.stickers);
        }
        if (!skipUserImage) {
            if (layerTree.photoStickers) {
                tempCtx.drawImage(layerTree.photoStickers, 0, 0);
            }
            drawUserImageWithTransforms(layerTree.userImage);
        }
    }

    // Independent both mode: each surface renders its own treatment
    if (isBothModeIndependent) {
        // Determine which elements have border/pattern/corner placement
        const gripPlacementTypes = [CYODesign.Placement.border, CYODesign.Placement.pattern, CYODesign.Placement.corner];
        const userImageHasGripPlacement = gripPlacementTypes.includes(cyoDesign?.subjectPlacement);
        const stickersHaveGripPlacement = cyoDesign?.stickerSet && gripPlacementTypes.includes(cyoDesign?.stickerPlacement);

        // tempCanvas already has all content drawn from above.
        // We need to restructure: full-bleed content stays, border/pattern/corner gets masked per-surface

        // Step 1: Rebuild tempCanvas with only full-bleed content (center placement)
        tempCtx.clearRect(0, 0, width, height);
        if (layerTree.bgImage) {
            tempCtx.drawImage(layerTree.bgImage, 0, 0);
        }
        // Draw center-placement stickers full-bleed
        if (!stickersHaveGripPlacement && layerTree.stickers) {
            drawStickersWithTransforms(layerTree.stickers);
        }
        // Draw center-placement user image full-bleed
        if (!userImageHasGripPlacement) {
            if (layerTree.photoStickers) {
                tempCtx.drawImage(layerTree.photoStickers, 0, 0);
            }
            drawUserImageWithTransforms(layerTree.userImage);
        }

        // Load wallet grip mask at print resolution
        const walletGripMaskImage = await loadWalletGripMask();
        const walletGripMaskCanvas = createScaledMask(walletGripMaskImage, width, height);
        const invertedWalletGripMaskCanvas = createInvertedMask(walletGripMaskImage, width, height);
        const gripBounds = getOpaqueBounds(invertedWalletGripMaskCanvas);

        if (gripBounds) {
            // Step 2: Create wallet-specific content (border/pattern/corner elements only, masked)
            const walletContentCanvas = document.createElement("canvas");
            walletContentCanvas.width = width;
            walletContentCanvas.height = height;
            const walletCtx = walletContentCanvas.getContext("2d", { alpha: true });

            if (stickersHaveGripPlacement && layerTree.stickers) {
                // Need to draw stickers to walletCtx with transforms
                if (layerTransformData && layerTransformData.stickers) {
                    const transform = layerTransformData.stickers;
                    if (transform.scale !== 1 || transform.rotate !== 0 || transform.translateX !== 0 || transform.translateY !== 0) {
                        walletCtx.save();
                        const centerX = width / 2;
                        const centerY = height / 2;
                        const scaledTranslateX = transform.translateX * transformScaleX;
                        const scaledTranslateY = transform.translateY * transformScaleY;
                        walletCtx.translate(centerX + scaledTranslateX, centerY + scaledTranslateY);
                        walletCtx.rotate(transform.rotate * Math.PI / 180);
                        walletCtx.scale(transform.scale, transform.scale);
                        walletCtx.translate(-centerX, -centerY);
                        walletCtx.drawImage(layerTree.stickers, 0, 0);
                        walletCtx.restore();
                    } else {
                        walletCtx.drawImage(layerTree.stickers, 0, 0);
                    }
                } else {
                    walletCtx.drawImage(layerTree.stickers, 0, 0);
                }
            }
            if (userImageHasGripPlacement) {
                if (layerTree.photoStickers) {
                    walletCtx.drawImage(layerTree.photoStickers, 0, 0);
                }
                // Draw user image with transforms to walletCtx
                if (layerTree.userImage) {
                    const canvas = layerTree.userImage;
                    let baseOffsetX = 0;
                    let baseOffsetY = 0;
                    if (canvas._isExpandedCanvas) {
                        baseOffsetX = -(canvas._viewportOffsetX || 0);
                        baseOffsetY = -(canvas._viewportOffsetY || 0);
                    }
                    if (layerTransformData && layerTransformData.userImage) {
                        const transform = layerTransformData.userImage;
                        if (transform.scale !== 1 || transform.rotate !== 0 || transform.translateX !== 0 || transform.translateY !== 0) {
                            walletCtx.save();
                            let centerX, centerY;
                            if (canvas._isExpandedCanvas) {
                                centerX = canvas._rotationPivotX ?? canvas._originalCanvasWidth / 2;
                                centerY = canvas._rotationPivotY ?? canvas._originalCanvasHeight / 2;
                            } else if (transform.visualCenterX !== undefined && transform.visualCenterY !== undefined) {
                                centerX = transform.visualCenterX * transformScaleX;
                                centerY = transform.visualCenterY * transformScaleY;
                            } else {
                                centerX = width / 2;
                                centerY = height / 2;
                            }
                            const scaledTranslateX = transform.translateX * transformScaleX;
                            const scaledTranslateY = transform.translateY * transformScaleY;
                            walletCtx.translate(centerX + scaledTranslateX, centerY + scaledTranslateY);
                            walletCtx.rotate(transform.rotate * Math.PI / 180);
                            walletCtx.scale(transform.scale, transform.scale);
                            walletCtx.translate(-centerX, -centerY);
                            walletCtx.drawImage(canvas, baseOffsetX, baseOffsetY);
                            walletCtx.restore();
                        } else {
                            walletCtx.drawImage(canvas, baseOffsetX, baseOffsetY);
                        }
                    } else {
                        walletCtx.drawImage(canvas, baseOffsetX, baseOffsetY);
                    }
                }
            }

            // Mask wallet-specific content to wallet area only
            if (stickersHaveGripPlacement || userImageHasGripPlacement) {
                walletCtx.globalCompositeOperation = "destination-in";
                walletCtx.drawImage(walletGripMaskCanvas, 0, 0);
                walletCtx.globalCompositeOperation = "source-over";
            }

            // Draw masked wallet content on top of full-bleed content
            tempCtx.drawImage(walletContentCanvas, 0, 0);
        }
    }

    // Draw the final masked content onto the print canvas
    // Draw the cropped region at 1:1 scale (no stretching/skewing)
    printCtx.drawImage(tempCanvas, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

    return printCanvas;
}
